import { useStaggeredAnimation } from "@/hooks/useScrollAnimation";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const testimonials = [
  {
    quote: "We feed 400 campers every session, and CampChef Pro saved us more than 40 hours per week in planning and ordering time. I can't imagine going back.",
    author: "Sarah Johnson",
    role: "Director",
    organization: "Pine Ridge Summer Camp",
    location: "North Carolina",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80",
    rotation: -2
  },
  {
    quote: "Sysco orders are generated automatically now — no spreadsheets, no late-night math. Our volunteers love how easy it is.",
    author: "Mike Chen",
    role: "Kitchen Manager",
    organization: "The Summit California",
    location: "California",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80",
    rotation: 1
  },
  {
    quote: "We serve diverse dietary needs every weekend, and CampChef Pro keeps it organized. Zero missed meals, zero stress.",
    author: "Elena Martinez",
    role: "Retreat Chef",
    organization: "Horizon Faith Center",
    location: "Texas",
    image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=400&q=80",
    rotation: -1
  },
  {
    quote: "CampChef Pro turned our chaos into calm. We finally have a real kitchen system that fits how camps actually work.",
    author: "Jacob Rivera",
    role: "Food Service Director",
    organization: "Camp Evergreen",
    location: "Oregon",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80",
    rotation: 2
  }
];

export const TestimonialsSection = () => {
  const { containerRef, visibleItems } = useStaggeredAnimation(testimonials.length, 150);

  return (
    <section className="py-20 bg-[#F5F1E8] relative overflow-hidden">
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(
            90deg,
            transparent,
            transparent 2px,
            rgba(139, 115, 85, 0.03) 2px,
            rgba(139, 115, 85, 0.03) 4px
          )`
        }}></div>
      </div>

      {/* Decorative camp elements */}
      <div className="absolute top-1/4 left-10 text-4xl opacity-10 rotate-12 hidden lg:block">
        🌲
      </div>
      <div className="absolute top-1/3 right-20 text-3xl opacity-10 -rotate-12 hidden lg:block">
        🔥
      </div>
      <div className="absolute bottom-1/4 left-1/4 text-3xl opacity-10 rotate-6 hidden lg:block">
        🍴
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-slate mb-4">
            Camp Stories That Inspire Us
          </h2>
          <p className="font-inter text-lg text-slate/80 max-w-4xl mx-auto">
            Real kitchens. Real teams. Real results. See how camps across the country are transforming their meal planning with CampChef Pro.
          </p>
        </div>

        {/* Polaroid-style testimonial cards */}
        <div 
          ref={containerRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 max-w-7xl mx-auto mb-12"
        >
          {testimonials.map((testimonial, index) => (
            <figure
              key={index}
              className={`relative bg-white rounded-sm p-3 shadow-2xl transition-all duration-700 hover:shadow-3xl hover:scale-105 hover:z-10 group ${
                visibleItems.has(index)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-12'
              }`}
              style={{ 
                transform: `rotate(${testimonial.rotation}deg)`,
                transitionDelay: `${index * 150}ms`
              }}
            >
              {/* Decorative Pin */}
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-copper/80 rounded-full shadow-lg z-10 flex items-center justify-center animate-float">
                <div className="w-3 h-3 bg-slate/30 rounded-full"></div>
              </div>
              
              {/* Washi Tape strip */}
              <div className="absolute -top-1 left-1/4 w-1/2 h-4 bg-gradient-to-r from-deep-teal/20 to-copper/20 -rotate-1 opacity-60"></div>

              {/* Photo Section */}
              <div className="mb-3 overflow-hidden rounded-sm aspect-[4/5] bg-slate/5">
                <img 
                  src={testimonial.image} 
                  alt={`${testimonial.author}, ${testimonial.role} at ${testimonial.organization}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Quote on Polaroid "paper" */}
              <figcaption className="px-2 py-3">
                <blockquote className="font-inter text-sm text-slate/90 leading-relaxed mb-4 italic">
                  "{testimonial.quote}"
                </blockquote>

                {/* Author Info */}
                <div className="border-t border-slate/10 pt-3">
                  <p className="font-inter font-semibold text-slate text-sm">
                    {testimonial.author}
                  </p>
                  <p className="font-inter text-xs text-slate/70">
                    {testimonial.role}
                  </p>
                  <p className="font-inter text-xs text-deep-teal font-medium mt-1">
                    {testimonial.organization}
                  </p>
                  <p className="font-inter text-xs text-slate/50">
                    📍 {testimonial.location}
                  </p>
                </div>
              </figcaption>

              {/* Camp badge corner */}
              <div className="absolute bottom-3 right-3 opacity-20">
                <svg className="w-6 h-6 text-deep-teal" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 3L4 9v12h16V9l-8-6zm0 2.5L17 10v9H7v-9l5-4.5z"/>
                </svg>
              </div>
            </figure>
          ))}
        </div>

        {/* Metrics Bar */}
        <div className="mt-20 bg-white/80 backdrop-blur-sm rounded-2xl border-2 border-deep-teal/20 p-8 shadow-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold mb-1">⏱️</div>
              <div className="font-playfair text-2xl font-bold text-slate mb-1">40+</div>
              <div className="font-inter text-sm text-slate/70">Hours Saved Monthly</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-1">🧾</div>
              <div className="font-playfair text-2xl font-bold text-slate mb-1">99.7%</div>
              <div className="font-inter text-sm text-slate/70">Order Accuracy</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-1">🍲</div>
              <div className="font-playfair text-2xl font-bold text-slate mb-1">50K+</div>
              <div className="font-inter text-sm text-slate/70">Meals Managed Daily</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-1">🏕️</div>
              <div className="font-playfair text-2xl font-bold text-slate mb-1">500+</div>
              <div className="font-inter text-sm text-slate/70">Trusted Camps Nationwide</div>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="mt-16 text-center bg-gradient-to-br from-deep-teal/10 to-copper/10 rounded-2xl p-12 border-2 border-deep-teal/20">
          <h3 className="font-playfair text-3xl md:text-4xl font-bold text-slate mb-4">
            Your Camp Could Be Our Next Story
          </h3>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button 
              asChild 
              size="lg" 
              className="bg-deep-teal hover:bg-deep-teal/90 text-ivory font-semibold shadow-lg"
            >
              <Link to="/contact">Request a Camp Demo</Link>
            </Button>
            
            <Button 
              asChild 
              size="lg" 
              variant="outline"
              className="border-2 border-deep-teal text-deep-teal hover:bg-deep-teal/5"
            >
              <Link to="/pricing">View Pricing & Request Demo</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
